import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import {
  buildSystemPrompt,
  BLOCK_PROMPT_BUILDERS,
  composeBlockOrder,
  BLOCK_META,
} from '@ellie/shared';
import type { BlockSlot, TopicCode, WorkbookProfile } from '@ellie/shared';
import { useStudioStore } from '@/stores/useStudioStore';

export function PromptEditor() {
  const {
    profile,
    systemPromptOverride,
    setSystemPromptOverride,
    blockPromptOverrides,
    setBlockPromptOverride,
    clearPromptOverrides,
  } = useStudioStore();

  const [showSystem, setShowSystem] = useState(false);
  const [expandedBlock, setExpandedBlock] = useState<number | null>(null);
  const [useCustomSystem, setUseCustomSystem] = useState(systemPromptOverride !== null);

  // Build the default system prompt for display
  const defaultSystemPrompt = useMemo(
    () =>
      buildSystemPrompt({
        profile: profile as unknown as Pick<
          WorkbookProfile,
          | 'native_language'
          | 'target_language'
          | 'level'
          | 'display_name'
          | 'learning_reason'
          | 'profession'
          | 'destination_city'
          | 'destination_country'
          | 'topics'
          | 'situations'
          | 'learning_focus'
        >,
      }),
    [profile],
  );

  // Compose block sequence for per-block prompts
  const slots = useMemo<BlockSlot[]>(() => {
    if (profile.topics.length === 0) return [];
    return composeBlockOrder({
      pace: profile.pace,
      topics: profile.topics as TopicCode[],
      workbookTheme: 'Preview',
      learningFocus: profile.learning_focus ?? undefined,
    });
  }, [profile.pace, profile.topics, profile.learning_focus]);

  // Build per-block prompts using the prompt builders
  const blockPrompts = useMemo(() => {
    const fakeProfile = {
      ...profile,
      id: 'studio',
      user_id: 'studio',
      shipping_name: null,
      shipping_address_line1: null,
      shipping_address_line2: null,
      shipping_city: null,
      shipping_postal_code: null,
      shipping_country: null,
      is_active: false,
      created_at: '',
      updated_at: '',
    } as WorkbookProfile;

    return slots.map((slot) => {
      const builder = BLOCK_PROMPT_BUILDERS[slot.type];
      return builder({
        profile: fakeProfile,
        workbookTheme: 'Preview',
        subTheme: slot.subTheme,
        blockOrder: slot.order,
        totalBlocks: slots.length,
        previousVocabulary: [],
        previousBlockSummaries: [],
      });
    });
  }, [slots, profile]);

  return (
    <div className="space-y-3">
      {/* Clear overrides button */}
      {(systemPromptOverride || Object.keys(blockPromptOverrides).length > 0) && (
        <button
          onClick={() => {
            clearPromptOverrides();
            setUseCustomSystem(false);
          }}
          className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
        >
          <RotateCcw size={12} />
          Reset all overrides
        </button>
      )}

      {/* System prompt section */}
      <div className="rounded-lg border border-border">
        <button
          onClick={() => setShowSystem(!showSystem)}
          className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-text-primary hover:bg-gray-50"
        >
          {showSystem ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          System Prompt
          {systemPromptOverride && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
              CUSTOM
            </span>
          )}
        </button>

        {showSystem && (
          <div className="border-t border-border p-3">
            <div className="mb-2 flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-text-muted">
                <input
                  type="checkbox"
                  checked={useCustomSystem}
                  onChange={(e) => {
                    setUseCustomSystem(e.target.checked);
                    if (!e.target.checked) {
                      setSystemPromptOverride(null);
                    } else {
                      setSystemPromptOverride(defaultSystemPrompt);
                    }
                  }}
                  className="rounded border-border"
                />
                Custom mode
              </label>
            </div>

            <textarea
              value={
                useCustomSystem
                  ? (systemPromptOverride ?? defaultSystemPrompt)
                  : defaultSystemPrompt
              }
              onChange={(e) => setSystemPromptOverride(e.target.value)}
              readOnly={!useCustomSystem}
              rows={12}
              className={`w-full rounded border border-border px-3 py-2 font-mono text-xs leading-relaxed ${
                useCustomSystem ? 'bg-white text-text-primary' : 'bg-gray-50 text-text-muted'
              }`}
            />
          </div>
        )}
      </div>

      {/* Per-block prompts */}
      <div className="rounded-lg border border-border">
        <div className="px-3 py-2 text-sm font-medium text-text-primary">
          Block Prompts ({slots.length})
        </div>

        <div className="border-t border-border divide-y divide-border">
          {slots.map((slot, i) => {
            const meta = BLOCK_META[slot.type];
            const isExpanded = expandedBlock === i;
            const hasOverride = !!blockPromptOverrides[slot.type];

            return (
              <div key={i}>
                <button
                  onClick={() => setExpandedBlock(isExpanded ? null : i)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                >
                  {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span className="text-xs font-medium text-text-light w-4">{slot.order}</span>
                  <span
                    className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold text-white"
                    style={{ backgroundColor: meta.color }}
                  >
                    {meta.name}
                  </span>
                  <span className="flex-1 truncate text-xs text-text-muted">{slot.subTheme}</span>
                  {hasOverride && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                      CUSTOM
                    </span>
                  )}
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3">
                    <div className="mb-1.5 flex items-center justify-between">
                      <label className="flex items-center gap-1.5 text-xs text-text-muted">
                        <input
                          type="checkbox"
                          checked={hasOverride}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBlockPromptOverride(slot.type, blockPrompts[i]);
                            } else {
                              setBlockPromptOverride(slot.type, null);
                            }
                          }}
                          className="rounded border-border"
                        />
                        Custom mode
                      </label>
                      {hasOverride && (
                        <button
                          onClick={() => setBlockPromptOverride(slot.type, null)}
                          className="text-[10px] text-amber-600 hover:text-amber-700"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <textarea
                      value={hasOverride ? blockPromptOverrides[slot.type]! : blockPrompts[i]}
                      onChange={(e) => setBlockPromptOverride(slot.type, e.target.value)}
                      readOnly={!hasOverride}
                      rows={8}
                      className={`w-full rounded border border-border px-3 py-2 font-mono text-xs leading-relaxed ${
                        hasOverride ? 'bg-white text-text-primary' : 'bg-gray-50 text-text-muted'
                      }`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
